import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Employee, FacultySchedule } from "../../../types/dataModel";
import CustomModal from "../../../core/common/customModal";
import Table from '../../../core/common/dataTable/index';
import useFetch from "../../../hooks/useFetch";
import { API } from "../../../common/Constants";
import { getAPIHeaders } from "../../../common/Utils";
import PreLoader from "../../../core/common/preLoader";

interface Props {
  employee?: Employee
  show: boolean
  onClose: () => void
}

type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

const dayOrder: DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

function parseFacultyTimetable(apiData: any) {
  const data = apiData.data;
  let maxPeriod = 0;
  dayOrder.forEach(day => {
    if (Array.isArray(data[day])) {
      data[day].forEach((period: any) => {
        if (period.periodNo > maxPeriod) maxPeriod = period.periodNo;
      });
    }
  });

  const rows = [];
  for (let i = 1; i <= maxPeriod; i++) {
    const row: Record<string, any> = {};
    dayOrder.forEach(day => {
      if (Array.isArray(data[day])) {
        row[day] = data[day].find((p: any) => p.periodNo === i) || null;
      } else {
        row[day] = null;
      }
    });
    rows.push(row);
  }
  return rows;
}

const FacultyScheduleModal = ({ employee, show, onClose }: Props) => {
  const [heading, setHeading] = useState<string>("Faculty Weekly Schedule ");
  const [schedule, setSchedule] = useState<FacultySchedule[]>([]);
  const { isLoading, callFetch } = useFetch();


  useEffect(() => {
    fetchSchedule();
    setHeading(`Weekly Schedule for ${employee?.firstName} ${employee?.lastName} `);
  }, [employee]);

  const fetchSchedule = () => {
    callFetch(API.FACULTY_TIMETABLE.replace(':employeeId', String(employee?.accountId)), 
        'GET', getAPIHeaders(), null, null).then((res) => {
      setSchedule(parseFacultyTimetable(res));
    }).catch((err) => {});
  };

  const renderCell = (record: any) => {
    if (!record) return null;
    return (
      <div className="d-flex flex-column gap-1 p-1 bg-light-subtle">
        <div className="fw-bold text-primary">{record.subject}</div>
        <div className="small">Period: <span className="fw-semibold">{record.periodNo}</span></div>
        <div className="small">Class: <span className="fw-semibold">{record.className}</span></div>
        <div className="small text-secondary">{record.time}</div>
      </div>
    );
  }

  const columns = [
        {
            title: 'Period',
            dataIndex: "period",
            render: (_: any, __: any, index: number) => (
                            <div className="text-center">{index + 1}</div>
            ),
            key: "period",
            fixed: 'left',
        },
        {
            title: 'Monday',
            dataIndex: 'className',
            render: (text: String, record: any) =>(
                renderCell(record.mon)
            ),
            sorter: (a: any, b: any) => (a?.className || '').localeCompare(b?.className || ''),
        },
        {
            title: 'Tuesday',
            dataIndex: 'className',
            render: (text: String, record: any) =>(
                renderCell(record.tue)
            ),
            sorter: (a: any, b: any) => (a?.className || '').localeCompare(b?.className || ''),
        },
        {
            title: 'Wednesday',
            dataIndex: 'className',
            render: (text: String, record: any) =>(
                renderCell(record.wed)
            ),
            sorter: (a: any, b: any) => (a?.className || '').localeCompare(b?.className || ''),
        },
        {
            title: 'Thursday',
            dataIndex: 'className',
            render: (text: String, record: any) =>(
                renderCell(record.thu)
            ),
            sorter: (a: any, b: any) => (a?.className || '').localeCompare(b?.className || ''),
        },
        {
            title: 'Friday',
            dataIndex: 'className',
            render: (text: String, record: any) =>(
                renderCell(record.fri)
            ),
            sorter: (a: any, b: any) => (a?.className || '').localeCompare(b?.className || ''),
        },
        {
            title: 'Saturday',
            dataIndex: 'className',
            render: (text: String, record: any) =>(
                renderCell(record.sat)
            ),
            sorter: (a: any, b: any) => (a?.className || '').localeCompare(b?.className || ''),
        },
        {
            title: 'Sunday',
            dataIndex: 'className',
            render: (text: String, record: any) =>(
                renderCell(record.sun)
            ),
            sorter: (a: any, b: any) => (a?.className || '').localeCompare(b?.className || ''),
        },


  ];

  return (
    <div>
      <PreLoader showLoader={isLoading} />
      <CustomModal
        size="xl"
        show={show}
        onClose={onClose}
        title={heading}
        bodyContent={
          
            <div className="overflow-auto" style={{ maxHeight: '60vh' }}>
            <Table
                  dataSource={schedule}
                  columns={columns}
                  Selection={false}
                  showSearch={false}
                  showTableTopData={false}
                />
            </div>
        
        }
        footerContent={
          <div className={"w-100 flex-column d-flex"}>
            <div className="align-self-end">
              <Link
                to="#"
                className="btn btn-light me-2"
                onClick={onClose}
              >
                Close
              </Link>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default FacultyScheduleModal;
